import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react'
import type { ChatMessage } from '@/types'
import { ChatAPI, fileToBase64 } from '@/utils/api'

export interface ChatInterfaceRef {
  clearChat: () => void
}

export interface ChatInterfaceProps {
  welcomeMessage?: string
  showHeader?: boolean
}

const chatAPI = new ChatAPI()

const ChatHeader = ({ onClear }: { onClear: () => void }) => (
  <div className="p-4 bg-primary text-white flex items-center justify-between">
    <div>
      <h1 className="text-xl font-bold">Asistente IA — Dr. Horcon</h1>
      <p className="text-xs text-white/80">Consultas generales. No reemplaza la atención presencial.</p>
    </div>
    <button
      type="button"
      onClick={onClear}
      className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs"
    >
      Limpiar chat
    </button>
  </div>
)

const ChatInterfaceComponent = forwardRef(function ChatInterface(
  { welcomeMessage = '¡Hola! Soy el Dr. Horcon, tu asistente veterinario, puedes compartirme una foto o hacerme una consulta. ¿En qué puedo ayudarte?', showHeader = true }: ChatInterfaceProps,
  ref: React.ForwardedRef<ChatInterfaceRef>,
) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: welcomeMessage,
      timestamp: new Date(),
    },
  ])
  const [inputValue, setInputValue] = useState('')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isSending])

  useEffect(() => {
    const stored = localStorage.getItem('chat_session_token')
    if (stored) {
      chatAPI.clearSession()
      chatAPI.setSessionToken(stored)
    }
  }, [])

  const persistSessionToken = (token: string | null) => {
    if (token) {
      localStorage.setItem('chat_session_token', token)
    } else {
      localStorage.removeItem('chat_session_token')
    }
    chatAPI.setSessionToken(token)
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const removeImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const sendMessage = async () => {
    if (!inputValue.trim() && !selectedImage) return

    const messageText = inputValue
    const currentImage = selectedImage
    setError(null)
    setIsSending(true)

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
      image: imagePreview || undefined,
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setSelectedImage(null)
    setImagePreview(null)

    try {
      const result = currentImage
        ? await chatAPI.sendMessage(messageText || 'Analizá esta imagen, por favor.', await fileToBase64(currentImage))
        : await chatAPI.sendMessage(messageText)

      persistSessionToken(result.sessionToken)

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: result.message,
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, assistantMessage])
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Hubo un error al procesar tu mensaje. Por favor, intentá nuevamente.'
      setError(message)
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: message,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsSending(false)
    }
  }

  const clearChat = () => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date(),
      },
    ])
    persistSessionToken(null)
    setError(null)
  }

  useImperativeHandle(ref, () => ({ clearChat }))

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {showHeader && <ChatHeader onClear={clearChat} />}

      {error && (
        <div className="px-4 py-2 bg-error/10 text-error text-xs">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-2 underline">Cerrar</button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                message.role === 'user' ? 'bg-primary text-white' : 'bg-background-alt text-text-primary'
              }`}
            >
              {message.image && (
                <img src={message.image} alt="User upload" className="max-w-full h-32 object-cover rounded mb-2" />
              )}
              <p className="whitespace-pre-wrap">{message.content}</p>
              <span className="text-xs opacity-70 block text-right mt-1">
                {message.timestamp.toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}

        {isSending && (
          <div className="flex justify-start">
            <div className="bg-background-alt text-text-primary rounded-lg px-4 py-2">
              <i className="ph ph-spinner animate-spin"></i>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {imagePreview && (
        <div className="p-2 bg-background border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <img src={imagePreview} alt="Preview" className="h-10 w-10 object-cover rounded" />
            <span className="text-sm text-text-secondary flex-1">Imagen seleccionada</span>
            <button
              type="button"
              onClick={removeImage}
              className="p-1 text-error hover:bg-error/10 rounded"
            >
              <i className="ph ph-x"></i>
            </button>
          </div>
        </div>
      )}

      <div className="p-4 bg-background border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-primary hover:bg-primary/10 rounded-full"
            title="Adjuntar imagen"
          >
            <i className="ph ph-image text-xl"></i>
          </button>

          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribí tu consulta..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            rows={1}
          />

          <button
            onClick={sendMessage}
            disabled={isSending || (!inputValue.trim() && !selectedImage)}
            className="p-2 text-white bg-primary rounded-full hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Enviar"
          >
            <i className="ph ph-paper-plane-right text-xl"></i>
          </button>
        </div>
      </div>
    </div>
  )
})

export default ChatInterfaceComponent